<template>
  <ReportLayoutModerno
    id="conferenceList"
    :prefecture="prefecture"
    :period="convertDate(filter.startDate) + ' até ' + convertDate(filter.endDate)"
    :showHeader="false"
  >
    <!-- ===== BLOCOS POR SECRETARIA/SETOR ===== -->
    <div
      v-for="(d, i) in data.supplies"
      :key="i"
      :class="(i > 0 && d.sec_name !== data.supplies[i - 1].sec_name) ? 'page-break' : ''"
    >
      <!-- CABEÇALHO COMPLETO: exibido na 1ª linha de cada novo setor -->
      <header
        v-if="i === 0 || d.sec_name !== data.supplies[i - 1].sec_name"
        class="rpt-header"
      >
        <div class="rpt-header-appname">{{ $store.getters.getAppName }}</div>
        <img
          v-if="prefecture"
          :src="prefecture.prefecture_logo ? $store.state.urlImages + prefecture.prefecture_logo : require('@/assets/no-image.jpg')"
          :alt="prefecture.prefecture_social_name || ''"
          class="rpt-header-logo"
        />
        <h1 class="rpt-header-title">{{ prefecture ? (prefecture.prefecture_social_name || '') : '' }}</h1>
        <div class="rpt-header-meta">
          <span>Período: <strong>{{ convertDate(filter.startDate) }} até {{ convertDate(filter.endDate) }}</strong></span>
          <span class="rpt-meta-sep">|</span>
          <span>Fornecedor: <strong class="rpt-provider">{{ provider && provider.provider_name ? provider.provider_name : '' }}</strong></span>
        </div>
        <div class="rpt-header-subtitle">
          Relatório Geral – Setor: <strong>{{ d.sec_name }}</strong>
        </div>
      </header>

      <!-- CABEÇALHO DO SETOR (barra escura) -->
      <div
        v-if="i === 0 || d.sec_name !== data.supplies[i - 1].sec_name"
        class="rpt-sector-bar"
      >
        <div>
          <div class="rpt-sector-bar-label">SECRETARIA / SETOR</div>
          <div class="rpt-sector-bar-name">{{ d.sec_name }}</div>
        </div>
        <v-icon color="orange" size="18">mdi-office-building-marker</v-icon>
      </div>

      <!-- TABELA: cada setor tem thead próprio para colunas alinhadas -->
      <table class="rpt-table">
        <thead v-if="i === 0 || d.sec_name !== data.supplies[i - 1].sec_name">
          <tr>
            <th class="rpt-col-ficha">FICHA<br>CONTÁBIL</th>
            <th class="rpt-col-veiculo">VEÍCULO / MODELO</th>
            <th class="rpt-col-placa">PLACA</th>
            <th class="rpt-col-comb">COMBUSTÍVEL</th>
            <th class="rpt-col-cupons">CUPONS</th>
            <th class="rpt-col-litros rpt-right">LITROS</th>
            <th class="rpt-col-preco rpt-right">PREÇO<br>LICITADO</th>
            <th class="rpt-col-total rpt-right">TOTAL</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="rpt-col-ficha rpt-ficha-cell">{{ d.subsec_desc || '---' }}</td>
            <td class="rpt-col-veiculo">
              <div class="rpt-brand">{{ d.brand_desc }}</div>
              <div class="rpt-model">{{ d.model_desc }}</div>
            </td>
            <td class="rpt-col-placa">
              <span class="rpt-plate">{{ d.vehicle_board }}</span>
            </td>
            <td class="rpt-col-comb">{{ d.item_description_desc }}</td>
            <td class="rpt-col-cupons">
              <span class="rpt-coupons">
                {{ d.vehiclesCoupons ? d.vehiclesCoupons.split(',').map(c => c.trim()).join('  ') : '—' }}
              </span>
            </td>
            <td class="rpt-col-litros rpt-right rpt-val">{{ d.totalLiters ? realMaskII(Number(d.totalLiters).toFixed(3)) : '0,000' }}</td>
            <td class="rpt-col-preco rpt-right rpt-val">{{ d.supply_bidding_price ? realMaskII(Number(d.supply_bidding_price).toFixed(3)) : '0,000' }}</td>
            <td class="rpt-col-total rpt-right rpt-money">R$ {{ d.totalValue ? realMask(Number(d.totalValue).toFixed(2)) : '0,00' }}</td>
          </tr>
        </tbody>

        <!-- SUBTOTAL DO SETOR: somente na última linha do grupo -->
        <tfoot v-if="i === data.supplies.length - 1 || data.supplies[i + 1].sec_name !== d.sec_name">
          <!-- Subtotal do Setor com detalhamento por combustível -->
          <tr class="rpt-subtotal-row">
            <td colspan="5" class="rpt-subtotal-label rpt-right">
              SUBTOTAL DO SETOR
              <span v-if="secFuelTotals(d.sec_name).length > 0" class="rpt-subtotal-fuels">
                (
                <span v-for="(fuel, fIdx) in secFuelTotals(d.sec_name)" :key="'fuel-' + fIdx">
                  <span class="rpt-fuel-item">
                    {{ fuel.item_description_desc }}: {{ realMaskII(Number(fuel.totalLiters).toFixed(3)) }} Lts - R$ {{ realMask(Number(fuel.totalValue).toFixed(2)) }}
                  </span>
                  <span v-if="fIdx < secFuelTotals(d.sec_name).length - 1"> | </span>
                </span>
                )
              </span>
              :
            </td>
            <td class="rpt-right rpt-subtotal-val">{{ realMaskII(Number(secTotalLiters(d.sec_name)).toFixed(3)) }}</td>
            <td></td>
            <td class="rpt-right rpt-subtotal-money">R$ {{ realMask(Number(secTotalValue(d.sec_name)).toFixed(2)) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>


    <!-- ===== RESUMO POR SECRETARIA ===== -->
    <div v-if="data.resumeBySecretaria" class="rpt-secsummary">
      <div class="rpt-secsummary-hdr">
        <v-icon color="orange" size="16">mdi-domain</v-icon>
        <span class="rpt-secsummary-title">RESUMO POR SECRETARIA</span>
      </div>
      <table class="rpt-secsummary-table">
        <thead>
          <tr>
            <th>SECRETARIA / SETOR</th>
            <th>COMBUSTÍVEL</th>
            <th class="rpt-right">VOLUME (Lts)</th>
            <th class="rpt-right">PREÇO LICITADO</th>
            <th class="rpt-right">VALOR TOTAL</th>
          </tr>
        </thead>
        <tbody
          v-for="(secData, secName, si) in data.resumeBySecretaria"
          :key="secName"
        >
          <tr
            v-for="(item, ii) in secData.items"
            :key="`si${si}ii${ii}`"
            :class="{ 'rpt-secsec-first': ii === 0 }"
          >
            <td v-if="ii === 0" :rowspan="secData.items.length" class="rpt-secname-cell">{{ secName }}</td>
            <td>{{ item.item_description_desc }}</td>
            <td class="rpt-right">{{ item.totalLiters ? realMaskII(Number(item.totalLiters).toFixed(3)) : '0,000' }}</td>
            <td class="rpt-right">{{ item.supply_bidding_price ? realMaskII(Number(item.supply_bidding_price).toFixed(3)) : '0,000' }}</td>
            <td class="rpt-right rpt-secmoney">R$ {{ item.totalValue ? realMask(Number(item.totalValue).toFixed(2)) : '0,00' }}</td>
          </tr>
          <tr class="rpt-secsubtotal">
            <td class="rpt-right rpt-secsubtotal-lbl" colspan="2">SUBTOTAL:</td>
            <td class="rpt-right rpt-secsubtotal-val">{{ realMaskII(Number(secData.totalLiters).toFixed(3)) }}</td>
            <td></td>
            <td class="rpt-right rpt-secsubtotal-money">R$ {{ realMask(Number(secData.totalValue).toFixed(2)) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </ReportLayoutModerno>
</template>

<script>
import helpers from "../../../mixins/helpers";
import ReportLayoutModerno from "@/components/ReportLayoutModerno.vue";

export default {
  components: { ReportLayoutModerno },
  props: ["data", "filter", "prefecture", "provider"],
  mixins: [helpers],
  mounted() {
    document.body.classList.add("white-bg-report");
    document.documentElement.classList.add("white-bg-report");
  },
  destroyed() {
    document.body.classList.remove("white-bg-report");
    document.documentElement.classList.remove("white-bg-report");
  },
  methods: {
    initialize() { this.helpers; },
    secTotalLiters(secName) {
      if (this.data.resumeBySecretaria && this.data.resumeBySecretaria[secName]) {
        return this.data.resumeBySecretaria[secName].totalLiters || 0;
      }
      if (!this.data || !this.data.supplies) return 0;
      return this.data.supplies
        .filter((s) => s.sec_name === secName)
        .reduce((sum, s) => sum + Number(s.totalLiters || 0), 0);
    },
    secTotalValue(secName) {
      if (this.data.resumeBySecretaria && this.data.resumeBySecretaria[secName]) {
        return this.data.resumeBySecretaria[secName].totalValue || 0;
      }
      if (!this.data || !this.data.supplies) return 0;
      return this.data.supplies
        .filter((s) => s.sec_name === secName)
        .reduce((sum, s) => sum + Number(s.totalValue || 0), 0);
    },
    secFuelTotals(secName) {
      if (!this.data || !this.data.supplies) return [];
      const fuelMap = {};
      this.data.supplies.forEach((supply) => {
        if (supply.sec_name === secName) {
          const key = supply.item_id;
          if (!fuelMap[key]) {
            fuelMap[key] = {
              item_id: supply.item_id,
              item_description_desc: supply.item_description_desc,
              totalLiters: 0,
              totalValue: 0,
            };
          }
          fuelMap[key].totalLiters += Number(supply.totalLiters || 0);
          fuelMap[key].totalValue += Number(supply.totalValue || 0);
        }
      });
      return Object.values(fuelMap);
    },
  },
};
</script>

<style>
/* =========================================================
   RELATÓRIO DE FATURAMENTO – LAYOUT PARA IMPRESSÃO
   Fonte: Arial | Paisagem A4 | Colunas fixas
   ========================================================= */

/* ── CONTROLE DINÂMICO DE FUNDO BRANCO ── */
.white-bg-report,
.white-bg-report .v-application,
.white-bg-report .v-application--wrap,
.white-bg-report .v-main,
.white-bg-report .v-main__wrap,
.white-bg-report .sci-page-wrapper {
  background-color: white !important;
}

/* ── FONTE GLOBAL ── */
#conferenceList,
#conferenceList * {
  font-family: Arial, Helvetica, sans-serif !important;
}

/* ── CABEÇALHO POR SETOR ── */
.rpt-header {
  text-align: center;
  padding: 10px 0 10px;
  margin-bottom: 8px;
  border-bottom: 2px solid #333;
}
.rpt-header-appname {
  font-size: 7.5px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.25em;
  margin-bottom: 6px;
}
.rpt-header-logo {
  height: 56px;
  object-fit: contain;
  display: block;
  margin: 0 auto 6px;
}
.rpt-header-title {
  font-size: 15px;
  font-weight: 900;
  color: #111;
  text-transform: uppercase;
  margin: 2px 0;
  letter-spacing: 0.04em;
}
.rpt-header-meta {
  font-size: 9.5px;
  color: #444;
  margin: 5px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.rpt-meta-sep { color: #bbb; }
.rpt-provider {
  color: #c45000;
  text-transform: uppercase;
}
.rpt-header-subtitle {
  font-size: 10px;
  font-weight: 700;
  color: #222;
  margin-top: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.rpt-header-subtitle strong {
  color: #c45000;
}

/* ── BARRA DO SETOR ── */
.rpt-sector-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #313131;
  padding: 5px 10px;
  margin-bottom: 0;
}
.rpt-sector-bar-label {
  font-size: 7.5px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  line-height: 1.2;
}
.rpt-sector-bar-name {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
}

/* ── TABELA PRINCIPAL ── */
.rpt-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin-bottom: 12px;
  border: 1px solid #ddd;
  border-top: none;
}

/* Larguras das colunas (total ~100% para landscape A4) */
.rpt-col-ficha   { width: 7%; }
.rpt-col-veiculo { width: 16%; }
.rpt-col-placa   { width: 8%; }
.rpt-col-comb    { width: 11%; }
.rpt-col-cupons  { width: 35%; }
.rpt-col-litros  { width: 8%; }
.rpt-col-preco   { width: 8%; }
.rpt-col-total   { width: 10%; }

/* Cabeçalho da tabela */
.rpt-table thead tr {
  background: #f0f0f0;
  border-bottom: 2px solid #bbb;
}
.rpt-table thead th {
  padding: 5px 6px;
  font-size: 8px;
  font-weight: 700;
  color: #444;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  line-height: 1.3;
  overflow: hidden;
}

/* Linhas do corpo */
.rpt-table tbody tr {
  border-bottom: 1px solid #eee;
}
.rpt-table tbody td {
  padding: 5px 6px;
  font-size: 9.5px;
  color: #222;
  vertical-align: middle;
  overflow: hidden;
  word-wrap: break-word;
}

/* Célula ficha contábil */
.rpt-ficha-cell {
  font-size: 8.5px;
  color: #777;
  font-style: italic;
  font-weight: 700;
  text-transform: uppercase;
}

/* Veículo */
.rpt-brand {
  font-size: 10px;
  font-weight: 700;
  color: #111;
  text-transform: uppercase;
  line-height: 1.2;
}
.rpt-model {
  font-size: 8.5px;
  color: #666;
  text-transform: uppercase;
  line-height: 1.2;
}

/* Placa: preto, simples, sem badge */
.rpt-plate {
  font-size: 10.5px;
  font-weight: 900;
  color: #111;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* Cupons: preto, texto corrido compacto */
.rpt-coupons {
  font-size: 9.5px;
  font-weight: normal;
  color: #111;
  font-family: Arial, Helvetica, sans-serif !important;
  word-break: normal;
  overflow-wrap: break-word;
  word-wrap: break-word;
  line-height: 1.5;
  white-space: normal;
}

/* Valores */
.rpt-val {
  font-size: 11px;
  font-weight: normal;
  color: #111;
}
.rpt-money {
  font-size: 11px;
  font-weight: normal;
  color: #111;
}
.rpt-right { text-align: right; }

/* Detalhamento de combustíveis no subtotal */
.rpt-subtotal-fuels {
  font-weight: normal;
  font-size: 8px;
  color: #666;
  margin-left: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.rpt-fuel-item {
  color: #c45000;
}

/* Subtotal do setor */
.rpt-subtotal-row {
  background: #f5f5f5;
  border-top: 2px solid #bbb;
}
.rpt-subtotal-row td {
  padding: 5px 6px;
}
.rpt-subtotal-label {
  font-size: 8.5px;
  font-weight: 700;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}
.rpt-subtotal-val {
  font-size: 11.5px;
  font-weight: normal;
  color: #111;
}
.rpt-subtotal-money {
  font-size: 11.5px;
  font-weight: normal;
  color: #006633;
}

/* ── RESUMO GERAL ── */
.rpt-summary {
  margin-top: 14px;
  border-radius: 6px;
  overflow: hidden;
  background: #222;
  color: #fff;
}
.rpt-summary-hdr {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(255,255,255,0.07);
}
.rpt-summary-eyebrow {
  font-size: 7.5px;
  font-weight: 700;
  color: #777;
  text-transform: uppercase;
  letter-spacing: 0.18em;
}
.rpt-summary-title {
  font-size: 11px;
  font-weight: 900;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}
.rpt-summary-table {
  width: 100%;
  border-collapse: collapse;
  color: #fff;
}
.rpt-summary-table thead th {
  padding: 6px 10px;
  font-size: 8px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  border-bottom: 1px solid rgba(255,255,255,0.07);
}
.rpt-summary-table tbody tr {
  border-top: 1px solid rgba(255,255,255,0.05);
}
.rpt-summary-table tbody td {
  padding: 6px 10px;
  font-size: 10px;
}
.rpt-summary-money {
  font-weight: normal;
}
.rpt-summary-total {
  border-top: 2px solid #f97316;
  background: rgba(255,255,255,0.05);
}
.rpt-summary-total-lbl {
  padding: 8px 10px;
  font-size: 10px;
  font-weight: 700;
  color: #aaa;
  text-transform: uppercase;
  font-style: italic;
}
.rpt-summary-total-val {
  padding: 8px 10px;
  font-size: 13px;
  font-weight: normal;
}
.rpt-unit {
  font-size: 8.5px;
  color: #777;
  text-transform: uppercase;
  margin-left: 3px;
}
.rpt-summary-total-money {
  padding: 8px 10px;
  font-size: 15px;
  font-weight: normal;
}
.rpt-currency {
  font-size: 9px;
  color: #777;
  margin-right: 2px;
}

/* ── RESUMO POR SECRETARIA ── */
.rpt-secsummary {
  margin-top: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
}
.rpt-secsummary-hdr {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #313131;
}
.rpt-secsummary-title {
  font-size: 9px;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.15em;
}
.rpt-secsummary-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
.rpt-secsummary-table thead th {
  padding: 5px 8px;
  font-size: 8px;
  font-weight: 700;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background: #f0f0f0;
  border-bottom: 2px solid #bbb;
}
.rpt-secsummary-table tbody td {
  padding: 4px 8px;
  font-size: 9.5px;
  color: #222;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}
.rpt-secname-cell {
  font-weight: 700;
  color: #111;
  text-transform: uppercase;
  background: #f8f8f8;
  border-right: 2px solid #ddd;
  vertical-align: top;
}
.rpt-secsec-first td {
  border-top: 2px solid #ccc;
}
.rpt-secmoney {
  font-weight: normal;
  color: #111;
}
.rpt-secsubtotal {
  background: #f0f0f0;
  border-top: 1px solid #bbb;
  border-bottom: 2px solid #bbb;
}
.rpt-secsubtotal td {
  padding: 4px 8px;
  font-size: 9px;
}
.rpt-secsubtotal-lbl {
  font-weight: 700;
  color: #555;
  text-transform: uppercase;
  font-size: 8.5px;
}
.rpt-secsubtotal-val {
  font-size: 11px;
  font-weight: normal;
  color: #111;
}
.rpt-secsubtotal-money {
  font-size: 11px;
  font-weight: normal;
  color: #006633;
}

/* ── IMPRESSÃO ── */
@media print {
  @page {
    size: A4 landscape;
    margin: 8mm 10mm 8mm 10mm;
  }

  html, body, #app, .v-application, .v-application--wrap, .v-main, .v-main__wrap, .sci-page-wrapper, .max-w-7xl {
    background: white !important;
    background-color: white !important;
  }

  .rpt-header { padding: 6px 0 8px; margin-bottom: 6px; }
  .rpt-header-logo { height: 44px; }
  .rpt-header-title { font-size: 13px; }
  .rpt-header-meta { font-size: 8.5px; }

  .rpt-sector-bar { padding: 4px 8px; }
  .rpt-sector-bar-name { font-size: 10px; }

  .rpt-table thead th { padding: 4px 5px; font-size: 7.5px; }
  .rpt-table tbody td { padding: 3px 5px; font-size: 9px; }
  .rpt-coupons { font-size: 7.5px; }

  .rpt-subtotal-row td { padding: 4px 5px; }
  .rpt-subtotal-money, .rpt-subtotal-val { font-size: 10px; }

  .rpt-summary { margin-top: 10px; }
  .rpt-summary-table thead th, .rpt-summary-table tbody td { padding: 4px 8px; }
  .rpt-summary-total-lbl, .rpt-summary-total-val, .rpt-summary-total-money { padding: 6px 8px; }
  .rpt-summary-total-money { font-size: 13px; }

  .rpt-secsummary { margin-top: 8px; }
  .rpt-secsummary-table thead th, .rpt-secsummary-table tbody td { padding: 3px 6px; }
}
</style>